import { redirect } from 'next/navigation'

// Import the proper browser-aware Supabase client
import { createClient } from './supabase/client'
const supabase = createClient()

// Get admin client from singleton - only for server-side use
import { SupabaseClientManager } from './supabase'
export const getSupabaseAdmin = () => SupabaseClientManager.getAdminInstance()

// Import client-side activity tracker
import { trackActivity } from './activity-tracker-client'

// Define user roles
export type UserRole = 'USER' | 'SALES' | 'FINANCE' | 'HR_SPECIALIST' | 'MANAGER' | 'ADMIN'

export interface UserProfile {
  id: string
  email: string
  first_name?: string
  last_name?: string
  avatar_url?: string
  phone?: string
  role: UserRole
  permissions: string[]
  is_active: boolean
  last_login_at?: string
  created_at: string
  updated_at: string
}

export interface UserClientAssignment {
  id: string
  user_id: string
  company_id: string
  assigned_at: string
}

// Authentication functions
export const signInWithEmail = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })
  
  if (error) {
    throw error
  }
  
  // Update last login time and track activity
  if (data.user) {
    await updateLastLogin(data.user.id)
    
    // Track login activity
    await trackActivity({
      activity_type: 'user_login',
      activity_category: 'system',
      entity_type: 'user',
      entity_id: data.user.id,
      entity_name: data.user.email || 'User',
      subject: `User logged in: ${data.user.email}`,
      description: `Login via email/password`,
      user_id: data.user.id,
      metadata: {
        method: 'email',
        ip_address: typeof window !== 'undefined' ? window.location.hostname : 'server',
        user_agent: typeof window !== 'undefined' ? navigator.userAgent : 'server'
      }
    })
  }
  
  return data
}

export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  })
  
  if (error) {
    throw error
  }
  
  return data
}

export const signUpWithEmail = async (email: string, password: string, firstName?: string, lastName?: string) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        first_name: firstName,
        last_name: lastName,
      }
    }
  })
  
  if (error) {
    throw error
  }
  
  return data
}

export const signOut = async () => {
  // Get current user before signing out
  const { data: { user } } = await supabase.auth.getUser()
  
  const { error } = await supabase.auth.signOut()
  
  if (error) {
    throw error
  }
  
  // Track logout activity if we had a user
  if (user) {
    await trackActivity({
      activity_type: 'user_logout',
      activity_category: 'system',
      entity_type: 'user',
      entity_id: user.id,
      entity_name: user.email || 'User',
      subject: `User logged out: ${user.email}`,
      description: `Manual logout`,
      user_id: user.id,
      metadata: {
        method: 'manual'
      }
    })
  }
  
  // Return success instead of redirecting (let caller handle navigation)
  return { success: true }
}

export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  return user
}

export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()
  
  if (error) {
    return null
  }
  
  return data
}

export const updateUserProfile = async (userId: string, updates: Partial<UserProfile>) => {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single()
  
  if (error) {
    throw error
  }
  
  return data
}

export const updateLastLogin = async (userId: string) => {
  const { error } = await supabase
    .from('profiles')
    .update({ last_login_at: new Date().toISOString() })
    .eq('id', userId)
  
  if (error) {
  }
}

// User management functions (for admins)
export const getAllUsers = async (): Promise<UserProfile[]> => {
  // Use admin client to bypass RLS for admin operations
  const supabaseAdmin = getSupabaseAdmin()
  const { data, error } = await supabaseAdmin
    .from('profiles')
    .select('*')
    .order('created_at', { ascending: false })
  
  if (error) {
    throw error
  }
  
  return data || []
}

export const createUser = async (email: string, password: string, userData: {
  first_name?: string
  last_name?: string
  role?: UserRole
}) => {
  // Create user in Supabase Auth using admin client
  const supabaseAdmin = getSupabaseAdmin()
  const { data: authData, error: authError } = await supabaseAdmin.auth.admin.createUser({
    email,
    password,
    email_confirm: true,
    user_metadata: {
      first_name: userData.first_name,
      last_name: userData.last_name,
    }
  })
  
  if (authError) {
    throw authError
  }
  
  // Update the profile with role information using admin client
  if (authData.user) {
    const { error: profileError } = await supabaseAdmin
      .from('profiles')
      .update({
        role: userData.role || 'USER',
        first_name: userData.first_name,
        last_name: userData.last_name,
      })
      .eq('id', authData.user.id)
    
    if (profileError) {
      throw profileError
    }
  }
  
  return authData.user
}

export const createUserProfile = async (profile: Omit<UserProfile, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('profiles')
    .insert([profile])
    .select()
    .single()
  
  if (error) {
    throw error
  }
  
  return data
}

export const deleteUserProfile = async (userId: string) => {
  const { error } = await supabase
    .from('profiles')
    .delete()
    .eq('id', userId)
  
  if (error) {
    throw error
  }
}

// Client assignment functions
export const getUserClientAssignments = async (userId: string): Promise<UserClientAssignment[]> => {
  const { data, error } = await supabase
    .from('user_client_assignments')
    .select('*')
    .eq('user_id', userId)
  
  if (error) {
    throw error
  }
  
  return data || []
}

export const assignUserToClient = async (userId: string, companyId: string) => {
  const { data, error } = await supabase
    .from('user_client_assignments')
    .insert([{
      user_id: userId,
      company_id: companyId
    }])
    .select()
    .single()
  
  if (error) {
    throw error
  }
  
  return data
}

export const removeUserFromClient = async (userId: string, companyId: string) => {
  const { error } = await supabase
    .from('user_client_assignments')
    .delete()
    .eq('user_id', userId)
    .eq('company_id', companyId)
  
  if (error) {
    throw error
  }
}

// Activity logging functions
export const logUserActivity = async (
  userId: string,
  action: string,
  resourceType: string,
  resourceId?: string,
  details?: any
) => {
  const { error } = await supabase
    .from('user_activity_log')
    .insert([{
      user_id: userId,
      action,
      resource_type: resourceType,
      resource_id: resourceId,
      details: details ? JSON.stringify(details) : null
    }])
  
  if (error) {
  }
}

export const getUserActivity = async (userId: string, limit: number = 50) => {
  const { data, error } = await supabase
    .from('user_activity_log')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit)
  
  if (error) {
    throw error
  }
  
  return data || []
}

export const getAllActivity = async (limit: number = 100) => {
  const { data, error } = await supabase
    .from('user_activity_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit)
  
  if (error) {
    throw error
  }
  
  return data || []
}

// Permission and role checking functions
export const hasPermission = (userRole: UserRole, requiredRole: UserRole): boolean => {
  const roleHierarchy: Record<UserRole, number> = {
    'USER': 0,
    'SALES': 1,
    'FINANCE': 2,
    'HR_SPECIALIST': 2,
    'MANAGER': 3,
    'ADMIN': 4,
  }

  return roleHierarchy[userRole] >= roleHierarchy[requiredRole]
}

export const canAccessFinancials = (userRole: UserRole): boolean => {
  return ['FINANCE', 'MANAGER', 'ADMIN'].includes(userRole)
}

export const canManageClients = (userRole: UserRole): boolean => {
  return ['SALES', 'MANAGER', 'ADMIN'].includes(userRole)
}

export const canManageUsers = (userRole: UserRole): boolean => {
  return ['MANAGER', 'ADMIN'].includes(userRole)
}

export const canAccessClient = async (userId: string, companyId: string, userRole: UserRole): Promise<boolean> => {
  // Admins and managers can access all clients
  if (['ADMIN', 'MANAGER'].includes(userRole)) {
    return true
  }
  
  // Check if user is assigned to this client
  const assignments = await getUserClientAssignments(userId)
  return assignments.some(assignment => assignment.company_id === companyId)
}

// Middleware helper for protected routes
export const requireAuth = async () => {
  const user = await getCurrentUser()
  
  if (!user) {
    redirect('/auth/signin')
  }
  
  return user
}

export const requireRole = async (requiredRole: UserRole) => {
  const user = await requireAuth()
  const profile = await getUserProfile(user.id)
  
  if (!profile || !hasPermission(profile.role, requiredRole)) {
    redirect('/auth/unauthorised')
  }
  
  return { user, profile }
}